import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Trash2, Pencil, Eye, X, Save } from "lucide-react";
import { API, authHeaders } from "./shared";
import BlastPreviewModal from "./BlastPreviewModal";

const typeLabel = (m) => {
  if (m.type === "client_blast") return "Client Blast";
  if (m.type === "sms_blast") return "SMS Blast";
  if (m.type === "sms_optin_invite") return "Opt-In Invite";
  if (m.channel === "sms") return "SMS";
  return "Email";
};

const BlockedMessagesTab = () => {
  const [messages, setMessages] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState("");
  const [previewing, setPreviewing] = useState(null); // blocked message being previewed

  const load = useCallback(() => {
    axios.get(`${API}/admin/blocked-messages`, authHeaders())
      .then((r) => setMessages(r.data))
      .catch((e) => console.error("Failed to load blocked messages:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveEdit = async (id) => {
    setBusy(id);
    try {
      await axios.put(`${API}/admin/blocked-messages/${id}`, editForm, authHeaders());
      toast.success("Message updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally { setBusy(""); }
  };

  const approve = async (m) => {
    const isSms = m.type === "sms_blast";
    if (!window.confirm(`Approve & SEND this ${isSms ? "SMS to your opted-in text list" : "Client Blast to your full email list"} now?\n\n"${isSms ? m.message : m.subject}"`)) return;
    setBusy(m.id);
    try {
      const r = await axios.post(`${API}/admin/blocked-messages/${m.id}/approve`, {}, authHeaders());
      toast.success(`Sent to ${r.data.sent} of ${r.data.total} subscribers`);
      setPreviewing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to approve");
      setPreviewing(null);
    } finally { setBusy(""); }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this blocked message permanently?")) return;
    try {
      await axios.delete(`${API}/admin/blocked-messages/${m.id}`, authHeaders());
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const inputCls = "w-full bg-brand-bg border border-brand-border text-brand-text font-sans text-sm px-3 py-2 focus:border-brand-gold outline-none";

  return (
    <div data-testid="blocked-messages-tab">
      <h2 className="font-serif text-2xl text-brand-text flex items-center gap-2 mb-2">
        <ShieldAlert size={20} className="text-brand-gold" /> Blocked Message Log
      </h2>
      <p className="font-sans text-xs text-brand-text-muted mb-6">
        Ecommerce, promo and supplier messages stopped before sending. Nothing here goes out unless you approve it yourself.
      </p>

      {messages.length === 0 ? (
        <p data-testid="blocked-empty" className="font-sans text-sm text-brand-text-muted border border-brand-border bg-brand-surface p-8 text-center">
          No blocked messages. Anything stopped by Ecommerce Safe Mode will appear here.
        </p>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <div key={m.id} data-testid={`blocked-msg-${m.id}`} className="border border-brand-border bg-brand-surface p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-sans text-[10px] uppercase tracking-wider px-2 py-1 border border-brand-gold/40 text-brand-gold bg-brand-gold/10">{typeLabel(m)}</span>
                <span className={`font-sans text-[10px] uppercase tracking-wider px-2 py-1 border ${m.status === "approved_sent" ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
                  {m.status === "approved_sent" ? "Approved & Sent" : "Blocked"}
                </span>
                <span className="font-sans text-xs text-brand-text-muted ml-auto">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="font-sans text-xs text-brand-text-muted"><span className="text-brand-text">Reason:</span> {m.reason || "Blocked by Safe Mode"}</p>
              <p className="font-sans text-xs text-brand-text-muted"><span className="text-brand-text">Destination:</span> {m.to || "—"}</p>

              {editingId === m.id ? (
                <div className="mt-3 space-y-2" data-testid={`blocked-edit-form-${m.id}`}>
                  <input data-testid="blocked-edit-subject" className={inputCls} value={editForm.subject} onChange={(e) => setEditForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Subject" />
                  <textarea data-testid="blocked-edit-message" rows={4} className={inputCls} value={editForm.message} onChange={(e) => setEditForm((f) => ({ ...f, message: e.target.value }))} placeholder="Message" />
                  <div className="flex gap-2">
                    <button data-testid="blocked-edit-save" onClick={() => saveEdit(m.id)} disabled={busy === m.id} className="flex items-center gap-1.5 px-4 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50"><Save size={12} /> Save</button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1.5 px-4 py-1.5 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold transition-colors"><X size={12} /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 border-l-2 border-brand-gold/40 pl-3" data-testid={`blocked-preview-${m.id}`}>
                  {m.subject && <p className="font-sans text-sm text-brand-text font-medium">{m.subject}</p>}
                  <p className="font-sans text-xs text-brand-text-muted whitespace-pre-line mt-1 max-h-24 overflow-y-auto">{m.message || m.summary}</p>
                </div>
              )}

              {editingId !== m.id && (
                <div className="flex gap-2 mt-3">
                  {m.type === "client_blast" && m.status !== "approved_sent" && (
                    <>
                      <button data-testid={`blocked-edit-${m.id}`} onClick={() => { setEditingId(m.id); setEditForm({ subject: m.subject || "", message: m.message || "" }); }} className="flex items-center gap-1.5 px-3 py-1.5 border border-brand-border text-brand-text-muted font-sans text-xs hover:border-brand-gold hover:text-brand-gold transition-colors"><Pencil size={12} /> Edit</button>
                      <button data-testid={`blocked-approve-${m.id}`} onClick={() => setPreviewing(m)} disabled={busy === m.id} className="flex items-center gap-1.5 px-3 py-1.5 border border-green-500/40 text-green-400 font-sans text-xs hover:bg-green-500/10 transition-colors disabled:opacity-50"><Eye size={12} /> Preview & Approve</button>
                    </>
                  )}
                  {(m.type === "sms_blast" || m.type === "sms_optin_invite") && m.status !== "approved_sent" && (
                    <button data-testid={`blocked-approve-${m.id}`} onClick={() => approve(m)} disabled={busy === m.id} className="flex items-center gap-1.5 px-3 py-1.5 border border-green-500/40 text-green-400 font-sans text-xs hover:bg-green-500/10 transition-colors disabled:opacity-50"><Eye size={12} /> Approve & Send SMS</button>
                  )}
                  <button data-testid={`blocked-delete-${m.id}`} onClick={() => remove(m)} className="flex items-center gap-1.5 px-3 py-1.5 border border-red-500/40 text-red-400 font-sans text-xs hover:bg-red-500/10 transition-colors"><Trash2 size={12} /> Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {previewing && (
        <BlastPreviewModal
          subject={previewing.subject || ""}
          message={previewing.message || ""}
          approving={busy === previewing.id}
          onApprove={() => approve(previewing)}
          onEdit={() => { setEditingId(previewing.id); setEditForm({ subject: previewing.subject || "", message: previewing.message || "" }); setPreviewing(null); }}
          onClose={() => setPreviewing(null)}
          onScheduled={() => { setPreviewing(null); load(); }}
        />
      )}
    </div>
  );
};

export default BlockedMessagesTab;
